import { useState, forwardRef } from 'react';
import cn from 'clsx';
import { makeStyles, DefaultTheme } from '@material-ui/styles';

import { PersonAvatar, Theme } from '@bizzabo/design-system';

import { extractInitials } from './utils';

type AvatarProps = {
    src?: string;
    alt?: string;
    className?: string;
    size?: number;
    width?: number;
    height?: number;
    firstName?: string;
    lastName?: string;
    variant?: 'circle' | 'square';
    borderRadius?: number;
    backgroundColor?: string;
    color?: 'string';
    userClick?: () => void;
    badge?: JSX.Element;
    children?: React.ReactNode;
};

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
    (
        {
            src,
            alt,
            children,
            className,
            size = 32,
            width,
            height,
            firstName,
            lastName,
            variant = 'circle',
            borderRadius,
            backgroundColor,
            color = '#fff',
            userClick = () => {},
            badge,
        },
        ref
    ) => {
        const hardcodedBackground = !src && firstName && lastName;
        const classes = useStyles({
            backgroundColor,
            color,
            hardcodedBackground: !!hardcodedBackground,
        });
        const [error, setError] = useState(false);
        const handleError = () => {
            setError(true);
        };

        let inner;
        if (src && !error) {
            inner = <img className={classes.img} src={src} alt={alt} onError={handleError} />;
        } else if (src && error) {
            inner = <PersonAvatar />;
        } else if (firstName && lastName) {
            inner = extractInitials(firstName, lastName);
        } else if (children) {
            inner = children;
        } else {
            inner = <PersonAvatar />;
        }

        const avatarBorderRadius = borderRadius
            ? borderRadius
            : variant === 'circle'
            ? '50%'
            : 'initial';

        return (
            <div className={cn(classes.avatarWrapper, badge && classes.avatarBadge)}>
                <div
                    className={cn(classes.avatar, className)}
                    onClick={userClick}
                    ref={ref}
                    style={{
                        minWidth: width ? width : size,
                        width: width ? width : size,
                        height: height ? height : size,
                        borderRadius: avatarBorderRadius,
                    }}
                >
                    {inner}
                </div>
                {badge && <div className={classes.badge}>{badge}</div>}
            </div>
        );
    }
);

Avatar.displayName = 'Avatar';

type UseStylesPropsType = {
    backgroundColor?: string;
    color?: string;
    hardcodedBackground?: boolean;
};

const useStyles = makeStyles<DefaultTheme, UseStylesPropsType>(
    (theme: Theme) => ({
        avatarWrapper: {
            display: 'inline-block',
        },
        avatarBadge: {
            position: 'relative',
        },
        avatar: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: ({ backgroundColor, hardcodedBackground }) =>
                backgroundColor
                    ? backgroundColor
                    : hardcodedBackground
                    ? theme.palette.greys['500']
                    : 'none',
            color: ({ color }) => color,
            position: 'relative',
        },
        img: {
            width: '100%',
        },
        badge: {
            position: 'absolute',
            top: 0,
            left: '100%',
            transform: 'translateX(-50%)',
        },
    }),
    { classNamePrefix: Avatar.displayName }
);

export default Avatar;
